/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isAxiosError } from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type {
  HomepageBlockResponse,
  Platform,
} from '../models/homepageBlockTypes';

const getStaticBaseUrl = (env: string): string =>
  env === 'prd'
    ? 'https://static.airasia.com'
    : 'https://static.stgairasia.com';

const mapPlatformToPathSegment = (platform: Platform): 'app' | 'web' => {
  return platform === 'mobile' ? 'app' : 'web';
};

export class FallbackHomepageApiService {
  private client: ApiClient;
  constructor(env: string, timeout: number = 10000) {
    this.client = CoreNetworking({
      baseUrl: getStaticBaseUrl(env),
      timeout,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  async getFallbackHomePage(
    platform: Platform,
    locale: string,
  ): Promise<HomepageBlockResponse> {
    const segment = mapPlatformToPathSegment(platform);
    try {
      const response = await this.client.get<HomepageBlockResponse>(
        `/mobile/cs-next/v1/content/${segment}/homepage/${locale}.json`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const status = error.response?.status ?? 'Network Error';
        const message = error.response?.data?.message ?? error.message;
        throw { code: String(status), message };
      }
      throw error;
    }
  }
}
